import React, { useCallback, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { ROUTES } from "../../constants/clientRoutes";
import { ROUTES as API_ROUTES } from "../../constants/api";
import { ACTION_DISPLAY_TYPES } from "../../constants/enums";
import { GetApiReceiptData } from "../../api/general";
import useErrorModal from "../../hooks/useErrorModal";

// types
import type { SendMoneyConfirmationLocationState } from "../../types/sendMoney";

// ui components
import SingleColumnLayout from "../../components/Layouts/SingleColumnLayout";
import TransferStatusHeader from "../../components/SendMoney/TransferStatusHeader";
import DisplaySection from "../../components/MoneyTransfer/DisplaySection";
import Action from "../../components/Common/Action";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";

const SendMoneyConfirmationScreen = (): React.ReactElement => {
  const history = useHistory();
  const { state } = useLocation<SendMoneyConfirmationLocationState | undefined>();
  const openErrorModal = useErrorModal();
  const [receiptLoading, setReceiptLoading] = useState(false);

  const result = state?.result;

  const handleDone = useCallback(() => {
    history.push(ROUTES.MY_CARD.INDEX);
  }, [history]);

  const handleReceipt = useCallback(async () => {
    if (!result?.downloadReceiptUrl) return;
    setReceiptLoading(true);
    try {
      await GetApiReceiptData(
        API_ROUTES.SEND_MONEY.RECEIPT(result.downloadReceiptUrl),
        `Brightwell_Mobile_Wallet_${result.sendMoneyTransferId}.pdf`
      );
    } catch (err) {
      openErrorModal(err);
    } finally {
      setReceiptLoading(false);
    }
  }, [result, openErrorModal]);

  if (!result) return <Redirect to={ROUTES.MY_CARD.INDEX} />;

  // A failed commit creates no transfer.
  const failed = !result.sendMoneyTransferId;

  const doneAction = (
    <Action
      onClick={handleDone}
      displayType={ACTION_DISPLAY_TYPES.PRIMARY}
      title={result.buttonText}
    />
  );

  return (
    <div className="v2 flow-screen v3 bwp-send-money-confirmation">
      <TransferStatusHeader
        title={result.pageTitle}
        iconUrl={result.pageIcon ? API_ROUTES.SEND_MONEY.ICON(result.pageIcon) : undefined}
      />

      <SingleColumnLayout centered={true}>
        <div className={`bwp-send-money-confirmation__content${failed ? "" : " bwp-send-money-confirmation__content--with-footer"}`}>
          <DisplaySection
            variant="plain"
            sections={result.sections}
            className={failed
              ? "text-center"
              : "bwp-display-section-summary"}
          />

          {result.disclaimers?.map((disclaimer) => (
            <p className="text-secondary leading-snug" key={disclaimer.superscript}>
              <sup>{disclaimer.superscript}</sup> {disclaimer.text}
            </p>
          ))}

          {failed && <div className="text-center mt-24">{doneAction}</div>}
        </div>
      </SingleColumnLayout>

      {!failed && (
        <div className="bwp-fixed-footer">
          <div className="container">
            <SingleColumnLayout centered={true}>
              {result.downloadReceiptUrl && (
                <Action
                  loading={receiptLoading}
                  disabled={receiptLoading}
                  onClick={handleReceipt}
                  displayType={ACTION_DISPLAY_TYPES.SECONDARY}
                  isReceipt
                  title={result.downloadReceiptText}
                />
              )}
              {doneAction}
            </SingleColumnLayout>
          </div>
        </div>
      )}

      <ApiErrorModal />
    </div>
  );
};

export default SendMoneyConfirmationScreen;